/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { Box, Grid } from '@mui/material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { Button, Col, Flex, Icons, Row, Space, Tag, Typography, useTheme } from '@jcm/design-system';
import { Control, UseFormGetValues, UseFormSetValue, UseFormUnregister } from 'react-hook-form';
import { CriarObrigacaoPayload } from '../../../../ts/types/ObrigacaoEtapa/Obrigacoes/Obrigacoes';
import styles from './index.module.scss';
import EditCalendarOutlinedIcon from '@mui/icons-material/EditCalendarOutlined';
import { obrigacaoEstaConcluida } from '../../../../utils/determinarAcessoObrigacoes';
import todasEtapasConcluidas from '../../../../utils/obrigacao/todasEtapasConcluidas';
import { TEtapa } from '../../../../ts/types/ObrigacaoEtapa/Etapas/Etapa';
import { TipoDadoEnum } from '../../../../ts/enums/TipoDadoEnum';

interface AcoesProps {
	etapas?: TEtapa[];
	setValue: UseFormSetValue<any>;
	getValues: UseFormGetValues<any>;
	control: Control<any>;
	unregister: UseFormUnregister<any>;
	usuarioAprovador: boolean;
	usuarioResponsavel: boolean;
	openAlterarVencimentoModal: () => void;
	openConcluirObrigacaoModal: () => void;
	openCancelarObrigacaoModal: () => void;
	data: CriarObrigacaoPayload;
	empresaId: string | number;
	onAttachClick?: () => void;
	aprovadoresSelecionados: number[];
	responsaveisSelecionados: number[];
	tipoDado?: TipoDadoEnum;
}

const AcoesAnexo: React.FC<AcoesProps> = ({
	etapas = [],
	getValues,
	usuarioAprovador,
	usuarioResponsavel,
	openAlterarVencimentoModal,
	openConcluirObrigacaoModal,
	openCancelarObrigacaoModal,
	data,
	onAttachClick,
	tipoDado,
}) => {
	const { colors } = useTheme();
	const [temporarilyDisabled, setTemporarilyDisabled] = useState(true);

	useEffect(() => {
		const timer = setTimeout(() => setTemporarilyDisabled(false), 1500);
		return () => clearTimeout(timer);
	}, []);

	const aprovacao = getValues('estadoStatusAprovada');
	const statusData = getValues('estadoStatusAlterarData');
	const cancelar = getValues('estadoStatusCancelamento');

	const isObrigacaoConcluida = data.obrigacaoConcluida || aprovacao === 1;
	const anexosLabel = usuarioResponsavel && !isObrigacaoConcluida ? 'Adicionar anexos' : 'Visualizar anexos';

	return (
		<Box className={styles.acoesContainer}>
			<Typography.Body size='large' strong>
				Ações
			</Typography.Body>
			<Grid container spacing={2} className={styles.espacamentoTop}>
				<Grid item>
					{todasEtapasConcluidas(etapas) && (
						<Button
							className={styles.concluir}
							variant='default'
							type='filled-tonal'
							icon={<Icons.CheckCircle style={{ fontSize: '24px' }} />}
							onClick={(ev) => {
								ev.preventDefault();
								openConcluirObrigacaoModal();
							}}
							disabled={
								temporarilyDisabled ||
								(!usuarioResponsavel && !(usuarioAprovador && aprovacao > 0 && aprovacao === 1)) ||
								obrigacaoEstaConcluida(aprovacao)
							}
						>
							{!usuarioAprovador || (usuarioAprovador && usuarioResponsavel)
								? tipoDado === TipoDadoEnum.Obrigacao
									? 'Concluir obrigação'
									: 'Concluir etapa'
								: tipoDado === TipoDadoEnum.Obrigacao
									? 'Analisar a conclusão da obrigação'
									: 'Analisar a conclusão da etapa'}
						</Button>
					)}
				</Grid>

				<Grid item>
					<Button
						className={styles.alterarVencimento}
						variant='default'
						type='filled-tonal'
						icon={<EditCalendarOutlinedIcon />}
						onClick={(ev) => {
							ev.preventDefault();
							openAlterarVencimentoModal();
						}}
						disabled={
							temporarilyDisabled ||
							(!usuarioResponsavel && !(usuarioAprovador && statusData > 0 && statusData === 1)) ||
							obrigacaoEstaConcluida(aprovacao)
						}
					>
						{statusData > 0 && statusData === 1 && usuarioAprovador
							? 'Analisar alteração de vencimento'
							: (!usuarioAprovador || (usuarioAprovador && usuarioResponsavel))
							? 'Alterar data de vencimento'
							: 'Analisar alteração de vencimento'}
					</Button>
				</Grid>
				<Grid item>
					<Button
						className={styles.cancelar}
						variant='error'
						type='filled-tonal'
						icon={<CancelOutlinedIcon />}
						onClick={(ev) => {
							openCancelarObrigacaoModal();
							ev.preventDefault();
						}}
						disabled={
							temporarilyDisabled ||
							(!usuarioResponsavel && !(usuarioAprovador && cancelar > 0 && cancelar === 1)) ||
							obrigacaoEstaConcluida(aprovacao)
						}
					>
						{!usuarioAprovador || (usuarioAprovador && usuarioResponsavel)
							? tipoDado === TipoDadoEnum.Obrigacao
								? 'Cancelar obrigação'
								: 'Cancelar etapa'
							: 'Analisar cancelamento'}
					</Button>
				</Grid>
				<Grid item>
					<Button
						className={styles.anexos}
						style={{ backgroundColor: colors.roxoContainer }}
						type='filled-tonal'
						icon={<Icons.AttachFile style={{ fontSize: '24px' }} />}
						onClick={onAttachClick}
						disabled={temporarilyDisabled}
					>
						{anexosLabel}
					</Button>
				</Grid>
			</Grid>

			{!!etapas?.length && !todasEtapasConcluidas(etapas) && (
				<Row>
					<Col lg={24} md={24} sm={24} className={styles.alertaEtapasConclusao}>
						<Tag variant='amarelo'>Finalize todas as etapas para concluir a obrigação principal.</Tag>
					</Col>
				</Row>
			)}

			<Space direction='vertical' style={{ marginTop: 16 }}>
				{data.justificativaNaoAprovacao && data.estadoStatusAprovada === 3 && (
					<Flex gap={8} align='center'>
						<Icons.Error style={{ color: '#ff4d4f' }} />
						<Typography.Body> Conclusão não aprovada: {data.justificativaNaoAprovacao}</Typography.Body>
					</Flex>
				)}
				{data.justificativaNaoAprovacaoDataAlterada && data.estadoStatusAlterarData === 3 && (
					<Flex gap={8} align='center'>
						<Icons.Error style={{ color: '#ff4d4f' }} />
						<Typography.Body>
							Alteração de prazo não aprovada: {data.justificativaNaoAprovacaoDataAlterada}
						</Typography.Body>
					</Flex>
				)}

				{data.justificativaNaoAprovarCancelamento && data.estadoStatusCancelamento === 3 && (
					<Flex gap={8} align='center'>
						<Icons.Error style={{ color: '#ff4d4f' }} />
						<Typography.Body>Cancelamento não aprovado: {data.justificativaNaoAprovarCancelamento}</Typography.Body>
					</Flex>
				)}
			</Space>
		</Box>
	);
};

export default AcoesAnexo;
